import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, MessageCircle, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { StatusBadge, whatsappLink, fmtDate, PageTitle, ReadyBadge } from "./shared";
import { LeadSheet } from "./LeadSheet";

const Filter = ({ value, onChange, options, label, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="h-11 rounded-xl border border-neutral-200 bg-white px-3 text-sm" data-testid={testId}>
    <option value="">{label}</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

export default function Leads() {
  const { user } = useAuth();
  const [filters, setFilters] = useState({ q: "", status: "", city: "", country: "", ready: false });
  const [openId, setOpenId] = useState(null);
  const { data, refetch, isLoading } = useQuery({
    queryKey: ["leads", filters],
    queryFn: () => api.get("/staff/leads", { params: filters }).then((r) => r.data),
    staleTime: 10_000,
  });
  const leads = data?.leads || [];
  const counts = useMemo(() => leads.reduce((m, l) => ({ ...m, [l.status]: (m[l.status] || 0) + 1 }), {}), [leads]);
  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));

  const exportCsv = async () => {
    try {
      const res = await api.get("/staff/leads-export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: "nri-leads.csv" });
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="staff-leads-page">
      <PageTitle
        right={user.role === "super_admin" && (
          <button onClick={exportCsv} className="flex h-10 items-center gap-2 rounded-full border border-neutral-300 bg-white px-4 text-sm font-medium hover:border-neutral-500" data-testid="leads-export-button">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        )}
      >
        Leads <span className="text-lg text-neutral-400" data-testid="leads-count">{leads.length}</span>
      </PageTitle>
      <div className="mb-4 flex flex-wrap gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <input value={filters.q} onChange={(e) => set("q")(e.target.value)} placeholder="Search name, email, phone, area" className="h-11 w-full rounded-xl border border-neutral-200 bg-white pl-9 pr-3 text-sm" data-testid="leads-search-input" />
        </div>
        <Filter value={filters.status} onChange={set("status")} options={data?.statuses || []} label="All statuses" testId="leads-status-filter" />
        <Filter value={filters.city} onChange={set("city")} options={data?.facets.cities || []} label="All cities" testId="leads-city-filter" />
        <Filter value={filters.country} onChange={set("country")} options={data?.facets.countries || []} label="All countries" testId="leads-country-filter" />
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
        <button onClick={() => set("ready")(!filters.ready)} className={`flex min-h-[36px] items-center gap-1.5 rounded-full border px-3 font-semibold transition-colors ${filters.ready ? "border-[#138808] bg-[#138808] text-white" : "border-[#138808]/40 text-[#0f6d06]"}`} data-testid="leads-ready-filter">
          <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-current" /> Presentations ready to send <span data-testid="leads-ready-count">{data?.ready_count ?? 0}</span>
        </button>
        {(data?.statuses || []).map((s) => counts[s] ? <span key={s} className="text-neutral-500"><StatusBadge status={s} /> {counts[s]}</span> : null)}
      </div>
      <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
        {isLoading && <p className="p-6 text-sm text-neutral-500">Loading…</p>}
        {!isLoading && !leads.length && <p className="p-10 text-center text-sm text-neutral-500" data-testid="leads-empty">No leads yet.</p>}
        <ul className="divide-y divide-neutral-100">
          {leads.map((l) => (
            <li key={l.id} className="grid cursor-pointer gap-2 px-4 py-4 transition-colors hover:bg-[#fbf8f3] md:grid-cols-[1.4fr_1.3fr_1.2fr_auto_auto] md:items-center" onClick={() => setOpenId(l.id)} data-testid={`lead-row-${l.id}`}>
              <div>
                <p className="font-semibold text-[#1f2328]">{l.contact.name}</p>
                <p className="text-xs text-neutral-500">{l.contact.residence_country} · {fmtDate(l.created_at)}</p>
                <div className="mt-1"><ReadyBadge badge={l.mm_badge} testId={`lead-ready-badge-${l.id}`} /></div>
              </div>
              <div className="text-sm text-neutral-700">
                {l.answers.property_type} · {l.answers.configuration}
                <p className="text-xs text-neutral-500">{l.answers.budget}</p>
              </div>
              <div className="text-sm text-neutral-700">
                {l.answers.city}{l.answers.area ? ` · ${l.answers.area}` : ""}
                <p className="text-xs text-neutral-500">Visit: {l.answers.visit_timing}</p>
              </div>
              <StatusBadge status={l.status} testId={`lead-status-${l.id}`} />
              <a href={whatsappLink(l, user.name)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="flex h-11 items-center justify-center gap-2 rounded-full bg-[#25D366] px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90" data-testid={`lead-whatsapp-${l.id}`}>
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </a>
            </li>
          ))}
        </ul>
      </div>
      <LeadSheet id={openId} statuses={data?.statuses || []} onClose={() => setOpenId(null)} onChanged={refetch} />
    </div>
  );
}
